import {
  Button,
  Callout,
  Card,
  CardLevel,
  Checkbox,
  Classes,
  Col,
  Drawer,
  FormGroup,
  Frow,
  Heading2,
  Input,
  Intent,
  ISelectOption,
  MultiSelect,
  PaddingLevel,
} from '@pinpointhq/thumbtack';
import * as React from 'react';
import { AllocationRun, IGeneralAllocationSettings } from '../../../javascript/models';
import { createToast } from '../../FlashToaster';
import { DrawerFooter } from '../../shared/DrawerFooter';
import { DrawerHeader } from '../../shared/DrawerHeader';
import { ConditionsEditor } from './ConditionsEditor';

export interface IGeneralAllocationOptions {
  currentCycleName: string;
  membershipOptions: ISelectOption[];
  jobProgressOptions: ISelectOption[];
  educationLevels: ISelectOption[];
  yearsOfGraduation: ISelectOption[];
  degreeSubjects: ISelectOption[];
  skillOptions: ISelectOption[];
}

interface IGeneralDrawer extends IGeneralAllocationOptions {
  initialSettings: IGeneralAllocationSettings;
  handleClose: () => void;
  fetchData: () => void;
}

const PREFERENCES = [
  { value: 'one', label: 'First preference' },
  { value: 'two', label: 'Second preference' },
  { value: 'three', label: 'Third preference' },
];

// The criteria for a general allocation (Allocations::General::Settings), starting from the last run's
export function GeneralDrawer({ initialSettings, handleClose, fetchData, ...options }: IGeneralDrawer) {
  const [settings, setSettings] = React.useState<IGeneralAllocationSettings>(initialSettings);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const update = (changes: Partial<IGeneralAllocationSettings>) => setSettings({ ...settings, ...changes });

  const togglePreference = (preference: string, checked: boolean) => {
    const preferences = checked
      ? [...settings.preferences, preference]
      : settings.preferences.filter((p) => p !== preference);
    update({ preferences: PREFERENCES.map((p) => p.value).filter((p) => preferences.includes(p)) });
  };

  const checkbox = (key: keyof IGeneralAllocationSettings, label: string) => (
    <Checkbox
      checked={settings[key] as boolean}
      onChange={(event) => update({ [key]: event.currentTarget.checked } as Partial<IGeneralAllocationSettings>)}
      label={label}
    />
  );

  const numberInput = (key: keyof IGeneralAllocationSettings, label: string, helperText: string) => (
    <FormGroup label={label} helperText={helperText}>
      <Input
        type="number"
        min={0}
        value={String(settings[key])}
        onChange={(event) => update({ [key]: Number(event.currentTarget.value) } as Partial<IGeneralAllocationSettings>)}
      />
    </FormGroup>
  );

  const multiSelect = (key: 'membership_statuses' | 'job_progress', items: ISelectOption[]) => (
    <MultiSelect
      items={items}
      selectedItems={items.filter((item) => settings[key].includes(item.value))}
      handleChange={(selected: ISelectOption[]) => update({ [key]: selected.map((item) => item.value) } as any)}
      fill={true}
      tagInputProps={{ tagProps: { minimal: true, className: Classes.MUTED } }}
    />
  );

  const handleRun = () => {
    setIsSubmitting(true);
    const run = new AllocationRun({ generalAllocation: true, settings });
    run.save().then((success) => {
      if (success) {
        createToast({ text: 'Allocation started. It runs in the background and this page will update.', type: 'success' });
        fetchData();
        handleClose();
      } else {
        setIsSubmitting(false);
        createToast({ text: 'Unable to start the allocation', type: 'error' });
      }
    });
  };

  return (
    <Drawer maxWidth={800} isOpen={true} onClose={handleClose}>
      <DrawerHeader title={`General allocation ${options.currentCycleName}`} handleClose={handleClose} />
      <Card paddingLevel={PaddingLevel.EXPANDED} level={CardLevel.NONE} style={{ overflowY: 'scroll' }}>
        <Callout intent={Intent.PRIMARY} className="mar-b-4">
          This works out an allocation for every eligible candidate in the current cycle and gives you a CSV to check.
          Nothing is sent to employers until you assign it, so you can change the settings and run it again as many
          times as you like.
        </Callout>

        <Heading2>Preferences</Heading2>
        <FormGroup
          label="Run a pass for"
          helperText="Each pass gives every candidate at most one job, using that preference. Untick them all to only top up empty jobs."
        >
          {PREFERENCES.map((preference) => (
            <Checkbox
              key={preference.value}
              checked={settings.preferences.includes(preference.value)}
              onChange={(event) => togglePreference(preference.value, event.currentTarget.checked)}
              label={preference.label}
            />
          ))}
        </FormGroup>
        <FormGroup>{checkbox('fall_back_to_other_preferences', "If nothing matches, try the candidate's other preferences")}</FormGroup>

        <Heading2 className="mar-t-4">Location and sector</Heading2>
        <FormGroup>
          {checkbox('match_region', 'Only use jobs in a region the candidate has an address in')}
          {checkbox('expand_regions', 'If nothing matches, widen to neighbouring regions')}
          {checkbox('expand_workstreams', 'If nothing matches, widen to related sectors')}
        </FormGroup>

        <Heading2 className="mar-t-4">Jobs</Heading2>
        <FormGroup label="Membership levels" helperText="Organisations marked as excluded from general allocation are always left out.">
          {multiSelect('membership_statuses', options.membershipOptions)}
        </FormGroup>
        <FormGroup label="Job stages">{multiSelect('job_progress', options.jobProgressOptions)}</FormGroup>
        <Frow gutters={16}>
          <Col md={{ width: '1-2' }}>
            {numberInput('large_job_headcount', 'Large job headcount', 'Jobs with at least this many places are capped')}
          </Col>
          <Col md={{ width: '1-2' }}>
            {numberInput('large_job_max_per_headcount', 'Cap per place for large jobs', 'Most candidates per place')}
          </Col>
        </Frow>

        <Heading2 className="mar-t-4">Top up</Heading2>
        <FormGroup>{checkbox('top_up', 'After the passes, top up jobs that have no applications yet')}</FormGroup>
        {settings.top_up && (
          <Frow gutters={16}>
            <Col md={{ width: '1-2' }}>
              {numberInput('top_up_per_headcount', 'Target per place', 'Candidates per place to top up to')}
            </Col>
            <Col md={{ width: '1-2' }}>
              {numberInput(
                'max_allocations_per_candidate',
                'Most allocations per candidate',
                'Candidates with this many already are skipped when topping up',
              )}
            </Col>
          </Frow>
        )}

        <Heading2 className="mar-t-4">Candidates</Heading2>
        <ConditionsEditor
          conditions={settings.conditions}
          onChange={(conditions) => update({ conditions })}
          educationLevels={options.educationLevels}
          yearsOfGraduation={options.yearsOfGraduation}
          degreeSubjects={options.degreeSubjects}
          skillOptions={options.skillOptions}
        />
      </Card>
      <DrawerFooter>
        <Frow justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
          <div>
            <Button minimal={true} text="Cancel" onClick={handleClose} />
          </div>
          <div>
            <Button text="Run allocation" intent={Intent.PRIMARY} onClick={handleRun} disabled={isSubmitting} />
          </div>
        </Frow>
      </DrawerFooter>
    </Drawer>
  );
}
